import { Component, Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { FileService } from '../services/file';

type PreviewKind = 'image' | 'pdf' | 'other';

/** Sniff the renderable kind from a filename (preferred) or URL. */
function detectKind(fileName: string, url = ''): PreviewKind {
  const src = (fileName || url).toLowerCase();
  if (/\.(png|jpe?g|gif|webp|bmp|svg)(\?|#|$)/.test(src)) return 'image';
  if (/\.pdf(\?|#|$)/.test(src)) return 'pdf';
  return 'other';
}

/**
 * App-wide file preview. Any component calls `open(guid, label?)`; this fetches a
 * fresh presigned URL, previews images and PDFs inline, and always offers
 * Open-in-new-tab + Download (authed, so it works for any format incl. docx).
 *
 * A single {@link FilePreviewComponent} mounted at the app root renders the dialog.
 */
@Injectable({ providedIn: 'root' })
export class FilePreviewService {
  private files = inject(FileService);
  private platformId = inject(PLATFORM_ID);

  visible = signal(false);
  loading = signal(false);
  downloading = signal(false);
  url = signal('');
  fileName = signal('');
  kind = signal<PreviewKind>('other');
  error = signal('');
  private guid = '';

  /** Open the preview for a stored file guid. `label` is an optional dialog title. */
  open(guid: string | null | undefined, label = '') {
    if (!guid) return;
    this.guid = guid;
    this.fileName.set(label || 'Document');
    this.kind.set(detectKind(label));
    this.url.set('');
    this.error.set('');
    this.loading.set(true);
    this.visible.set(true);

    this.files.info(guid).subscribe({
      next: (info) => {
        this.loading.set(false);
        this.url.set(info.url);
        this.fileName.set(label || info.fileName || 'Document');
        // The server filename carries the real extension — best for type sniffing.
        this.kind.set(detectKind(info.fileName || label, info.url));
        if (!info.url) this.error.set('Could not load this file.');
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load this file.');
      },
    });
  }

  close() {
    this.visible.set(false);
    this.url.set('');
  }

  openInNewTab() {
    if (this.url() && isPlatformBrowser(this.platformId)) window.open(this.url(), '_blank');
  }

  /** Force-save via the authed /Download stream so any file type downloads reliably. */
  download() {
    if (!this.guid || this.downloading()) return;
    this.downloading.set(true);
    this.files.download(this.guid).subscribe({
      next: (blob) => {
        this.downloading.set(false);
        if (!isPlatformBrowser(this.platformId)) return;
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = objUrl;
        a.download = this.fileName() || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
      },
      error: () => this.downloading.set(false),
    });
  }
}

@Component({
  selector: 'app-file-preview',
  standalone: true,
  imports: [DialogModule, ButtonModule],
  template: `
    <p-dialog
      [visible]="svc.visible()"
      (visibleChange)="svc.visible.set($event)"
      [modal]="true"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="true"
      (onHide)="svc.close()"
      appendTo="body"
      [header]="svc.fileName()"
      styleClass="file-preview-dialog"
      [style]="{ width: '900px' }"
      [breakpoints]="{ '960px': '96vw' }"
    >
      <div class="fp-body">
        @if (svc.loading()) {
          <div class="fp-state"><i class="pi pi-spin pi-spinner"></i><p>Loading…</p></div>
        } @else if (svc.error()) {
          <div class="fp-state error"><i class="pi pi-exclamation-triangle"></i><p>{{ svc.error() }}</p></div>
        } @else if (svc.kind() === 'image') {
          <img class="fp-img" [src]="svc.url()" [alt]="svc.fileName()" />
        } @else if (svc.kind() === 'pdf') {
          <iframe class="fp-frame" [src]="safeUrl()" title="PDF preview"></iframe>
        } @else {
          <div class="fp-state">
            <i class="pi pi-file"></i>
            <p>No inline preview for this file type.</p>
            <small class="muted">Use Open or Download below to view it.</small>
          </div>
        }
      </div>

      <div class="fp-foot">
        <p-button label="Open in new tab" icon="pi pi-external-link" severity="secondary" [text]="true"
          [disabled]="!svc.url()" (onClick)="svc.openInNewTab()" />
        <p-button [label]="svc.downloading() ? 'Downloading…' : 'Download'"
          [icon]="svc.downloading() ? 'pi pi-spin pi-spinner' : 'pi pi-download'"
          [disabled]="svc.downloading() || !svc.url()" (onClick)="svc.download()" />
      </div>
    </p-dialog>
  `,
  styles: [
    `
      :host ::ng-deep .file-preview-dialog .p-dialog-content { padding: 0; }
      .fp-body {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 56vh;
        max-height: 72vh;
        padding: 12px;
        background: #f1f5f9;
        overflow: auto;
      }
      .fp-img {
        max-width: 100%;
        max-height: 70vh;
        object-fit: contain;
        border-radius: 8px;
        box-shadow: 0 6px 20px rgba(15, 23, 42, 0.18);
      }
      .fp-frame {
        width: 100%;
        height: 70vh;
        border: none;
        border-radius: 8px;
        background: #fff;
      }
      .fp-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        color: #6b7280;
        text-align: center;
        padding: 48px 20px;
      }
      .fp-state i { font-size: 36px; color: #007cc2; }
      .fp-state.error i { color: #dc2626; }
      .fp-state p { margin: 0; }
      .fp-state .muted { color: #9ca3af; }
      .fp-foot {
        display: flex;
        justify-content: flex-end;
        gap: 10px;
        padding: 12px 16px;
        border-top: 1px solid #e7ecef;
        background: #fff;
      }
    `,
  ],
})
export class FilePreviewComponent {
  svc = inject(FilePreviewService);
  private sanitizer = inject(DomSanitizer);

  /** Bypass-sanitized presigned URL for the PDF <iframe>. */
  safeUrl = computed<SafeResourceUrl>(() =>
    this.sanitizer.bypassSecurityTrustResourceUrl(this.svc.url()),
  );
}
